import { Dispatch, SetStateAction } from 'react';
import { Control, UseFormHandleSubmit } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputType as JoyInputType, NotificationPosition } from 'core/enums';
import { useGetLevels, useGetRequestStatuses } from 'support/hooks';
import { IRequest, IRequestForm, IRequestType } from 'support/types';
import { getRequestTypeChoices } from 'support/utils';

import {
  Confirmation,
  Button,
  Field,
  Notification,
  Visible,
  withQueryState,
} from 'coreOld/components';
import { Grid, Icon } from 'coreOld/elements';
import { ButtonType, InputType, Position, Size } from 'coreOld/enums';
import { IOption } from 'coreOld/types';
import { getRequiredLabel } from 'coreOld/utils';

import {
  ButtonWrapper,
  GridContainer,
  SectionTitle,
  WrapperNotification,
} from './requestDetailsFormStyle';

interface IProps {
  request?: IRequest;
  handleSubmit: UseFormHandleSubmit<IRequestForm>;
  onSubmit: (data: IRequestForm, skipLevelCheck?: boolean) => void;
  control: Control<IRequestForm>;
  onTypeChange: (typeId: number) => void;
  onRequiredDateChange: (value: string) => void;
  isSubmitting: boolean;
  isEditable: boolean;
  setIsEditable: Dispatch<SetStateAction<boolean>>;
  onCancel?: () => void;
  showLevelChangeConfirmation?: boolean;
  setShowLevelChangeConfirmation?: Dispatch<SetStateAction<boolean>>;
  requestTypes?: IRequestType[];
}

const RequestDetailsFormView = ({
  request,
  handleSubmit,
  onSubmit,
  control,
  onTypeChange,
  onRequiredDateChange,
  isSubmitting,
  isEditable,
  setIsEditable,
  onCancel,
  showLevelChangeConfirmation,
  setShowLevelChangeConfirmation,
  requestTypes,
}: IProps) => {
  const { t } = useTranslation();

  const isSaveEnabled = Boolean(request);
  const isDisabled = !isEditable && Boolean(request);
  const labelPosition = Position.LEFT;

  const disabledState = {
    disabled: isDisabled,
    editable: false,
  };

  return (
    <>
      {showLevelChangeConfirmation && (
        <Confirmation
          isOpen={showLevelChangeConfirmation}
          title={t('titleChangeSupportRequestLevel')}
          size={Size.MD}
          message={t('warningSupportRequestLevelChange')}
          confirmBtnLabel={t('buttonLabelContinue')}
          confirmBtnType={ButtonType.DANGER}
          onConfirm={handleSubmit((data) => onSubmit(data, true))}
          onCancel={() => setShowLevelChangeConfirmation(false)}
        />
      )}

      <WrapperNotification>
        <Notification position={NotificationPosition.TAB} />
      </WrapperNotification>

      <SectionTitle>
        {t('labelDetails')}
        <Visible when={!isDisabled && !isSubmitting}>
          <ButtonWrapper>
            <Button
              btnType={ButtonType.ACTION_INVERTED}
              onClick={onCancel}
              disabled={!isSaveEnabled}
              hasMarginRight
            >
              {t('buttonLabelCancel')}
            </Button>
            <Button onClick={handleSubmit((data) => onSubmit(data))} disabled={!isSaveEnabled}>
              {t('buttonLabelSave')}
            </Button>
          </ButtonWrapper>
        </Visible>
        <Visible when={isDisabled}>
          <div onClick={() => setIsEditable(true)}>
            <Icon name={Icon.PENCIL} hover />
          </div>
        </Visible>
      </SectionTitle>

      <GridContainer container isDisabled={isDisabled} spacing={2}>
        <Grid item md={6} xs={12}>
          <Field
            name="statusId"
            type={InputType.SELECT}
            label={t('labelStatus')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              useGetOptions: useGetRequestStatuses,
              getValue: (option: IOption) => option.id,
              getLabel: (option: IOption) => option.name,
              placeholder: t('labelSelectStatus'),
              size: Size.MD,
              fontInherit: isDisabled,
              ...disabledState,
            }}
            wrapperStyle={{ fontSize: 16 }}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Field
            name="type"
            type={JoyInputType.RADIO_GROUP}
            label={getRequiredLabel(t('labelSupportRequestType'))}
            inputProps={{
              control,
              choices: getRequestTypeChoices(requestTypes),
              onChange: (event) => onTypeChange(event.target.value),
              disabled: isDisabled,
            }}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Field
            name="customerCompanyName"
            type={InputType.TEXTFIELD}
            label={getRequiredLabel(t('labelCustomerCompanyName'))}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Field
            name="levelId"
            type={InputType.SELECT}
            label={t('labelSupportRequestLevel')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              useGetOptions: useGetLevels,
              getValue: (option: IOption) => option.id,
              getLabel: (option: IOption) => option.name,
              placeholder: t('labelSelectSupportRequestLevel'),
              size: Size.MD,
              fontInherit: isDisabled,
              ...disabledState,
            }}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Field
            name="customerNumber"
            label={t('labelCustomerNumber')}
            labelPosition={labelPosition}
            type={InputType.TEXTFIELD}
            inputProps={{
              control,
              ...disabledState,
            }}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Field
            name="customerNumberNotRequired"
            type={InputType.CHECKBOX}
            inputProps={{
              label: t('buttonLabelSupportRequestProspect'),
              style: {
                paddingTop: 12,
              },
              control,
              ...disabledState,
            }}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Field
            name="customerFirstName"
            label={getRequiredLabel(t('labelCustomerFirstName'))}
            labelPosition={labelPosition}
            type={InputType.TEXTFIELD}
            inputProps={{
              control,
              ...disabledState,
            }}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Field
            name="customerLastName"
            label={getRequiredLabel(t('labelCustomerLastName'))}
            labelPosition={labelPosition}
            type={InputType.TEXTFIELD}
            inputProps={{
              control,
              ...disabledState,
            }}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Field
            name="customerEmail"
            label={getRequiredLabel(t('labelCustomerEmail'))}
            labelPosition={labelPosition}
            type={InputType.TEXTFIELD}
            inputProps={{
              control,
              ...disabledState,
            }}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Field
            name="requiredByDate"
            label={getRequiredLabel(t('labelRequestedDate'))}
            labelPosition={labelPosition}
            type={InputType.DATE_INPUT}
            inputProps={{
              control,
              size: Size.MD,
              minDate: new Date(),
              onChange: onRequiredDateChange,
              showBorder: true,
              ...disabledState,
            }}
          />
        </Grid>
      </GridContainer>
    </>
  );
};

export default withQueryState(RequestDetailsFormView);
